// askSmart-ui/src/pages/MapleCoin/operations/IntegrityPanel.tsx
//
// Ledger integrity, institutionally: the latest recorded verification and
// what has been sealed since. A full verification (the whole seal chain, every
// balance projection against its entries, control accounts, and debits =
// credits) runs only when an Operations user asks — never on page load — and
// is recorded with who ran it. Hashes are not shown here.
import { useState } from 'react';
import { ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';
import { IntegrityCheck, IntegrityStatus, OpsApiError, runIntegrityCheck } from './opsApi';
import { Busy, Identity, Panel, dateTime } from './shared';

export default function IntegrityPanel({ status, onChecked }: { status: IntegrityStatus; onChecked: (c: IntegrityCheck) => void }) {
  const [running, setRunning] = useState(false);
  const [failed, setFailed] = useState(false);
  const check = status.latestCheck;
  const since = Number(status.sealsSinceCheck ?? '0');

  const verify = async () => {
    setRunning(true);
    setFailed(false);
    try {
      onChecked(await runIntegrityCheck());
    } catch (err) {
      setFailed(!(err instanceof OpsApiError) || err.status >= 500);
    } finally {
      setRunning(false);
    }
  };

  const Icon = !check ? ShieldQuestion : check.status === 'VERIFIED' ? ShieldCheck : ShieldAlert;
  const tone = !check ? 'muted' : check.status === 'VERIFIED' ? 'ok' : 'bad';
  return (
    <Panel title="Ledger integrity" label="Ledger integrity" actions={
      <button type="button" className="mco-btn" onClick={verify} disabled={running}>
        <Busy busy={running}>{running ? 'Verifying' : 'Verify now'}</Busy>
      </button>}>
      <div className={`mco-integrity mco-integrity--${tone}`} data-testid="integrity-status">
        <Icon size={22} />
        <div>
          {!check && <><strong>Not yet verified</strong><p>No full verification has been recorded. Run one to check the seal chain and balances.</p></>}
          {check?.status === 'VERIFIED' && <><strong>Verified</strong><p>No seal-chain or balance problems detected.</p></>}
          {check?.status === 'PROBLEMS_FOUND' && <><strong>Problems found</strong><p>The last verification found problems. Investigate before relying on the ledger.</p></>}
        </div>
      </div>
      {check && (
        <dl className="mco-kv mco-kv--inline">
          <div><dt>Verified at</dt><dd>{dateTime(check.checkedAt)}</dd></div>
          <div><dt>By</dt><dd><Identity sub={check.requestedBy.sub} /></dd></div>
          <div><dt>Seals checked</dt><dd className="mco-num">{check.sealsChecked}</dd></div>
          <div><dt>Through journal</dt><dd className="mco-num">#{check.lastJournalSeq ?? '—'}</dd></div>
          <div><dt>Sealed since</dt><dd className="mco-num">{since === 0 ? 'None' : `${since} journal${since === 1 ? '' : 's'}`}</dd></div>
        </dl>
      )}
      {check && check.problems.length > 0 && (
        <ul className="mco-problems">
          {check.problems.map((p, i) => (
            <li key={i}>{p.problem}{p.journalSeq ? ` (journal #${p.journalSeq})` : ''}</li>
          ))}
        </ul>
      )}
      {failed && <p className="mco-error" role="alert">The verification could not be run. Please try again.</p>}
    </Panel>
  );
}
